import React, { useMemo } from 'react';
import { CgFilterSelect } from 'modules/Common/ui/CgFilterDropdown';
import { useTranslation } from 'react-i18next';

// Generic single-select v2 field rendered with CgFilterSelect (Radix popover)
// instead of react-select. Use this for selects inside CgDialog modals:
// react-select portals its menu to document.body, outside the dialog's layer,
// so menu clicks registered as outside-interactions (see CountrySelectField
// for the same swap). Form state stores the option's value as a string —
// matching SearchSelectField's default storeAs='value' contract, so it's a
// drop-in for that common case.
export default function FilterSelectField({
  field,
  fieldState,
  id,
  options = [],
  valueKey = 'value',
  labelKey = 'label',
  placeholder,
  disabled,
  searchable,
  // Pass false where an empty value is not a state the form can be in — a wizard answer that has
  // already been counted as ready, for instance. Left undefined it reaches CgFilterSelect's own
  // default parameter, which is `true`.
  isClearable,
  allowCreate,
  // Label for the create row; CgFilterSelect's default reads `Use "…"`.
  formatCreateLabel,
}) {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? t('common:fields.selectPlaceholder', 'Select...');
  const normalized = useMemo(
    () =>
      (options || []).map((o) => ({
        value: String(o[valueKey] ?? ''),
        label: o[labelKey] ?? o.name ?? String(o[valueKey] ?? ''),
      })),
    [options, valueKey, labelKey],
  );

  const value =
    field.value === undefined || field.value === null || field.value === ''
      ? ''
      : String(field.value);

  return (
    <CgFilterSelect
      id={id}
      options={normalized}
      value={value}
      onChange={(v) => field.onChange(v ?? '')}
      placeholder={resolvedPlaceholder}
      disabled={disabled}
      error={!!fieldState?.error}
      searchable={searchable}
      isClearable={isClearable}
      allowCreate={allowCreate}
      formatCreateLabel={formatCreateLabel}
    />
  );
}
